import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { FeeType } from '@acorn-portal/shared/src/services/cancellation-api';
import {
	selectCancellation,
	selectPaymentEligibility,
	selectRateData,
} from '@acorn-portal/shared/src/store/cancellation/cancellation.selectors';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, styled, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import ActionButtonWrapper from './components/action-button-wrapper';
import Heading from './components/heading';
import ErrorModal from './components/modal/error-modal';
import LeaveModal from './components/modal/journey-switch/leave-modal';
import QuoteExpiredModal from './components/modal/quote-expired';
import PolicyInformation from './components/policy-information';
import RefundInformation from './components/refund-information';
import Section from './components/section';
import Subheading from './components/sub-heading';
import WaivedBalanceNotice from './components/waived-balance-notice';
import ContentWrapper from './components/wrapper/content-wrapper';
import PageWrapper from './components/wrapper/page-wrapper';
import { useCancellationDateDisplay } from './hooks/use-cancellation-date-display';
import { useConfirmCancellation } from './hooks/use-confirm-cancellation';
import { useLeaveCancellation } from './hooks/use-leave-cancellation';
import { shouldShowQuoteExpiredModal } from './utils/should-show-quote-expired-modal';
import { getPhoneNumber } from '../../utils/get-phone-numbers';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

const ChangeDateLink = styled(Link)(({ theme }) => ({
	color: theme.palette.secondary.main,
	display: 'inline-block',
	paddingTop: '0.5rem',
	paddingBottom: '0.5rem',

	'&:hover': {
		color: theme.palette.secondary.dark,
	},
}));

const CancellationConfirmation = () => {
	const [leaveModalOpen, setLeaveModalOpen] = useState(false);
	const [quoteExpiredModalOpen, setQuoteExpiredModalOpen] = useState(false);
	const { handleLeaveCancellation, isLeaving } = useLeaveCancellation();
	const { handleConfirmCancellation, isConfirming, confirmFailed, clearConfirmError } = useConfirmCancellation();
	const policy = useAppSelector((state) => state.fullPolicy);
	const cancellation = useAppSelector(selectCancellation);
	const rateData = useAppSelector(selectRateData);
	const { eligibleForRefund, eligibleForClearBalance } = useAppSelector(selectPaymentEligibility);
	const { showImmediateCancellationMessage, requested, actual } = useCancellationDateDisplay();
	const isQuoteExpired = shouldShowQuoteExpiredModal(cancellation.cancellationRatingCommand?.cancellationDate);
	const contactNumber = getPhoneNumber(policy.product, 'CONTACT');
	const { track } = useTrack();

	const timedCancellationDateMessage = actual ? (
		<>
			You asked for policy to end at <strong>{requested.time}</strong> on the <strong>{requested.date}</strong>. Your
			policy will remain active until <strong>{actual.time}</strong> on the <strong>{actual.date}</strong>.
		</>
	) : (
		<>
			Your policy will end at <strong>{requested.time}</strong> on the <strong>{requested.date}</strong>.
		</>
	);

	const handleConfirmClick = () => {
		if (isQuoteExpired) {
			setQuoteExpiredModalOpen(true);
		} else {
			track('cancellation_confirmation_confirm_button', { step_number: 3 });
			handleConfirmCancellation();
		}
	};

	useEffect(() => {
		track('cancellation_step_3_started', { name: 'cancellation_confirmation_page', step_number: 3 });
	}, []);

	const onLeaveClick = () => {
		track('cancellation_confirmation_leave_button', { step_number: 3 });
		setLeaveModalOpen(true);
	};

	const onBack = () => {
		track('cancellation_confirmation_back_button', { step_number: 3 });
	};

	return (
		<>
			<LoadingModal open={isConfirming} message='Confirming cancellation' />
			<PageWrapper>
				<ContentWrapper>
					<Heading>Confirmation</Heading>
					<Section>
						<Subheading>Your policy</Subheading>
						<PolicyInformation
							vehicleMake={policy.vehicleDetails.make}
							vehicleModel={policy.vehicleDetails.model}
							registrationNumber={policy.vehicleDetails.registration}
							policyNumber={policy.policyNumber}
						/>
					</Section>
					<Section>
						<Subheading>Cancellation date</Subheading>
						<Typography>
							{showImmediateCancellationMessage
								? 'After you confirm your cancellation, your policy will be cancelled within 15 minutes.'
								: timedCancellationDateMessage}
						</Typography>
						<ChangeDateLink to={ROUTE_PATHS.cancellationDate}>Change cancellation date</ChangeDateLink>
					</Section>
					{eligibleForRefund && (
						<Section>
							<Subheading>Your refund</Subheading>
							<RefundInformation
								refundAmount={rateData?.finalAmount ?? 0}
								cancellationFee={rateData?.cancellationFee ?? 0}
								totalRefundAmount={rateData?.initialAmount ?? 0}
								insurancePremiumTax={rateData?.insurancePremiumTax ?? 0}
								feeType={rateData?.feeType ?? FeeType.CancellationFee}
							/>
						</Section>
					)}
					{eligibleForClearBalance && <WaivedBalanceNotice />}
					<Alert severity='warning' variant='outlined' sx={{ mt: '40px', borderRadius: '4px' }}>
						Once your policy has been cancelled, we won&apos;t be able to recover it for you.
					</Alert>
					<ActionButtonWrapper sx={{ marginTop: '64px' }}>
						<Button
							startIcon={<ArrowBack />}
							component={Link}
							to={ROUTE_PATHS.cancellationDate}
							variant='text'
							size='medium'
							sx={{ color: 'primary.dark' }}
							onClick={onBack}
						>
							Go back
						</Button>
						<Button
							size='medium'
							variant='contained'
							type='submit'
							disabled={isConfirming}
							onClick={handleConfirmClick}
						>
							Confirm cancellation
						</Button>
						<QuoteExpiredModal open={quoteExpiredModalOpen} />
					</ActionButtonWrapper>
					<Box display='flex' justifyContent='center' marginTop='24px'>
						<Button
							variant='text'
							onClick={onLeaveClick}
							sx={{
								border: 'solid 1px',
								borderColor: 'primary.dark',
								color: 'primary.dark',
								px: '1rem',
								width: '100%',
							}}
						>
							I don&apos;t want to cancel
						</Button>
					</Box>
				</ContentWrapper>
			</PageWrapper>
			<LeaveModal
				open={leaveModalOpen}
				onClose={() => setLeaveModalOpen(false)}
				onLeave={handleLeaveCancellation}
				isLeaving={isLeaving}
				stepNumber={3}
			/>
			<ErrorModal open={confirmFailed} onClose={clearConfirmError} contactNumber={contactNumber} />
		</>
	);
};

export default CancellationConfirmation;
